import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { DEFAULT_MAP_CENTER } from '../../utils/constants'
import { pinFor, addTiles } from './mapPins'

// The pin art moved to `mapPins.js` when `TrackingMap` arrived and needed the same
// drawings. Same SVGs, same anchors — nothing about this component's behaviour changed.

/**
 * Imperative Leaflet map.
 *
 * `interactive` is the difference between the two places this is used. The
 * preview inside a form block is a picture — dragging and zooming are off, so a
 * thumb scrolling the page never gets captured by the map. The full-screen
 * picker is the opposite: tap anywhere to drop the pin, or drag the pin itself.
 */
export default function LeafletMap({
  lat,
  lng,
  onPick,
  variant = 'pickup',
  interactive = false,
  className = '',
}) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)

  // Held in a ref so the click handler, registered once, always calls the
  // current callback instead of the one captured on mount.
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick

  // Set when the coordinates changed because of something done on the map
  // itself. A tap or a drag has already put the pin where the person wanted it,
  // so recentring on the way back through would only move the ground under
  // their hand. A search result or a pasted link still recentres.
  const fromMapRef = useRef(false)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current, {
      zoomControl: interactive,
      dragging: interactive,
      scrollWheelZoom: interactive,
      touchZoom: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      tap: interactive,
    }).setView(lat != null ? [lat, lng] : DEFAULT_MAP_CENTER, lat != null ? 16 : 13)

    addTiles(map)

    if (interactive) {
      map.on('click', (event) => {
        fromMapRef.current = true
        onPickRef.current?.(event.latlng.lat, event.latlng.lng)
      })
    }

    mapRef.current = map

    // The container is often still being laid out when the map is created —
    // inside a sheet that has just opened, it may have had no height at all.
    const settle = setTimeout(() => map.invalidateSize(), 60)
    const onResize = () => map.invalidateSize()
    window.addEventListener('resize', onResize)

    return () => {
      clearTimeout(settle)
      window.removeEventListener('resize', onResize)
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive])

  // Marker follows the coordinates, wherever they came from: a search result, a
  // pasted link, a tap on the map, or the pin being dragged.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (lat == null || lng == null) {
      if (markerRef.current) {
        map.removeLayer(markerRef.current)
        markerRef.current = null
      }
      return
    }

    if (!markerRef.current) {
      markerRef.current = L.marker([lat, lng], {
        icon: pinFor(variant),
        draggable: interactive,
        keyboard: interactive,
        autoPan: interactive,
        title: interactive ? 'Drag to move the pin' : undefined,
      }).addTo(map)

      if (interactive) {
        markerRef.current.on('dragend', (event) => {
          const next = event.target.getLatLng()
          fromMapRef.current = true
          onPickRef.current?.(next.lat, next.lng)
        })
      }
    } else {
      markerRef.current.setLatLng([lat, lng])
    }

    if (fromMapRef.current) {
      fromMapRef.current = false
      return
    }

    map.setView([lat, lng], Math.max(map.getZoom(), 16))
  }, [lat, lng, variant, interactive])

  return (
    <div
      ref={containerRef}
      className={`bg-surface-2 ${className}`}
      // Leaflet needs a real height before it will render tiles.
      style={{ minHeight: '120px' }}
    />
  )
}
